import { useEffect, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { Figure, Flow } from '../components/Diagram';
import { UiPath } from '../components/UiPath';

const FILE_TO_LEDGER_FIGURE = (
  <Figure
    title="From file to ledger"
    caption="Review writes nothing to the ledger; the commit, one client at a time, is the step that creates invoices and subscriptions."
  >
    <Flow
      steps={[
        {
          fork: [
            { title: 'Spreadsheet upload', text: 'Map columns, check the dry-run preview.' },
            { title: 'Google Drive sync', text: 'Extraction reads each invoice document.' },
          ],
        },
        { title: 'Review', text: 'Accept, correct or discard. Nothing is billed.', tone: 'muted' },
        {
          title: 'Commit one client',
          text: 'Needs every one of its invoices answered.',
          tone: 'accent',
        },
        {
          title: 'Invoices and subscriptions',
          text: 'Invoices created, then the implied plan applied.',
          tone: 'accent',
        },
        { title: 'Backfill analytics', text: 'Run over the imported date range.' },
      ]}
    />
  </Figure>
);

const STEPS: {
  title: string;
  figure?: ReactNode;
  body: string;
  entity?: string;
  entityLabel?: string;
  where?: [string, ...string[]];
  whereNote?: string;
}[] = [
  {
    title: 'Why import history at all',
    body: 'MRR, NRR, and churn only mean something with a baseline. Importing your billing history — even just the last 12–24 months — lets analytics show real trends from day one instead of a chart that starts today. Imported invoices carry a source marker (Excel / Google Drive) so they stay distinguishable from system-generated ones. The invoices are also the evidence Revvly uses to reconstruct the subscriptions behind them, so you do not have to invent the plan shape in a spreadsheet first.',
    entity: 'invoice',
    entityLabel: 'Invoice',
  },
  {
    title: 'Spreadsheet route: upload and map',
    body: 'Import → Transactions accepts your Excel/CSV export. Revvly analyzes the columns and proposes a mapping (dates, amounts, currencies, customer names); you confirm or correct it, review the dry-run preview with per-row validation, then run the import. Subscriptions can be bulk-imported the same way under Import → Subscriptions.',
    where: ['Import', 'Transaction Import'],
  },
  {
    title: 'Google Drive route: connect and sync',
    body: 'Settings → Integrations connects a Drive account; you pick the folders where invoices live and start a sync. An extraction job reads each document and pulls out the structured fields — invoice number, amount, currency, dates. Failed extractions can be retried per document or per job.',
    where: ['Settings', 'Integrations'],
    whereNote:
      'Once folders are connected, Import is where you start a sync and pick a review back up.',
  },
  {
    title: 'Settle invoices Revvly already holds',
    body: 'A transaction import row can name an invoice that is already in Revvly, for example one imported from a PDF or from a client folder that arrived without a payment date. When that row carries a Payment Date, the import records the payment on the existing invoice instead of creating a second one. A row that says the invoice was part paid books only the amount still missing. Importing the same file again records nothing twice. A row marked Paid with no payment date leaves the invoice unpaid, because there is no date to book the payment on. The result step shows how many existing invoices were marked paid from their payment date.',
    where: ['Import', 'Transaction Import'],
    whereNote: 'Map the Payment Date column in Map Columns for this to apply.',
  },
  {
    title: 'Files from client folders and email',
    body: 'With client folders in Google Drive turned on, a file in a client’s folder, or an email attachment filed on the client, can be marked Invoice or Credit note on the client’s Documents tab. Marked files go through the same review described below, and nothing is billed until a person commits them. An attachment from a private email thread is never imported. When the review is ready, the bell tells the person who started the import, or the workspace admins for files Revvly picked up from client folders.',
    where: ['Clients', 'open a client', 'Documents', 'Mark'],
    whereNote: 'Setting up the folders is covered in Client folders in Google Drive.',
  },
  {
    title: 'Review what was extracted',
    body: 'Each sync result is reviewed before anything touches your ledger: accept it, correct extracted fields, or discard it as not-an-invoice (restorable if you change your mind). The queue is ordered as a curriculum — one product shape at a time, covering every billing shape first rather than emptying a pile invoice by invoice — so you are not asked about every invoice that looks the same. Confirming writes your choice onto the staged result and nothing else; subscriptions and invoices are created later by the commit step. You can stop at any point. Import shows a resume banner for a half-reviewed job instead of making you start again.',
    where: ['Import', 'open the sync job', 'Review'],
    whereNote:
      'Review one client at a time from the commit summary if you want to finish a single customer rather than follow the curriculum across the whole job.',
  },
  {
    title: 'Rebuild the subscriptions those invoices imply',
    body: "Importing a customer with years of billing history used to mean deciding the plan shape by hand, then hoping the invoices would attach. Revvly now clusters a client's own invoices into the subscriptions they imply, proposes the catalog versions those charges match, and can create both the catalog and the subscriptions. The commit unit is the client: a client's subscriptions are reconstructed from all of its invoices at once, so a client with invoices still unanswered cannot be committed. Confirming review still bills nothing; committing is the write that creates the invoices, then reads the plan those invoices imply, then applies exactly that plan. Where a charge describes a product you do not sell yet, the review offers to create the catalog rows that charge implies — a new product with its price history, or the missing past versions of an existing one — rather than leaving you to reconstruct the relationship in a spreadsheet.",
    entity: 'subscription',
    entityLabel: 'Subscription',
    where: ['Import', 'open the sync job', 'Summary'],
    whereNote:
      'Commit one client at a time once every one of its invoices has been answered. A catalog gap is offered from the review card when none of the existing products fit.',
    figure: FILE_TO_LEDGER_FIGURE,
  },
  {
    title: 'Link the unmatched, resolve the discrepancies',
    body: "Imports rarely arrive perfectly attributed. Unmatched invoices are queryable and can be attached to their subscription with linkInvoiceToSubscription. Where an imported amount disagrees with what the subscription's pricing says it should have been, an invoice discrepancy is raised — resolve them one by one or in bulk once you've confirmed which number is right.",
    entity: 'invoice',
    entityLabel: 'Invoice',
  },
  {
    title: 'Backfill the analytics',
    body: 'Historical metrics are computed from daily snapshots. After a large import, run backfillAnalyticsSnapshots over the imported date range (up to three years per run) so MRR and ARR charts incorporate the history you just brought in.',
    entity: 'lifecycleEvent',
    entityLabel: 'Lifecycle Event',
  },
];

export function ImportInvoicesGuide() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Import historical invoices — Revvly Docs';
  }, []);

  return (
    <div className="tn-page">
      <nav className="dp-crumbs" aria-label="Breadcrumb">
        <Link to="/docs/guides">Guides</Link>
        <span aria-hidden="true">›</span>
        <span className="dp-crumb-current">Import historical invoices</span>
      </nav>

      <header className="dp-header">
        <span className="rv-eyebrow-mini">Guide</span>
        <h1>
          Import <em>historical</em> invoices.
        </h1>
        <p className="dp-oneliner">
          Bring billing history in from Excel or Google Drive — extraction, a curriculum-ordered
          review, the subscriptions those invoices imply, and the analytics backfill that makes your
          charts start before today.
        </p>
      </header>

      {STEPS.map((step, i) => (
        <div key={i} className="dp-section">
          <h2>
            <span style={{ color: 'var(--rv-fg-dark-5)', fontWeight: 400, marginRight: 12 }}>
              {String(i + 1).padStart(2, '0')}
            </span>
            {step.title}
          </h2>
          <p className="dp-plain">{step.body}</p>
          {step.figure}
          {step.where && <UiPath steps={step.where} note={step.whereNote} />}
          {step.entity && (
            <ul className="dp-connects" style={{ marginTop: 14 }}>
              <li>
                <Link
                  to={`/docs/concepts/${step.entity}`}
                  style={{ color: 'inherit', textDecoration: 'none' }}
                >
                  {step.entityLabel} concept ›
                </Link>
              </li>
            </ul>
          )}
        </div>
      ))}

      <ul className="dp-connects">
        <li>
          <Link
            to="/docs/guides/drive-folders"
            style={{ color: 'inherit', textDecoration: 'none' }}
          >
            Client folders in Google Drive ›
          </Link>
        </li>
      </ul>
    </div>
  );
}
